import type { ScheduleResult } from "@planerius/contracts/cabinet"
import { useCallback, useEffect, useState } from "react"

import type { JournalQuery } from "../../api/journal.api"
import { fetchJournal } from "../../api/journal.api"
import type { ApiProblem } from "../../api/problem"
import { isApiProblem, transportProblem } from "../../api/problem"

/**
 * Загрузка дня журнала.
 *
 * ПОЧЕМУ ПРЕЖНИЙ ДЕНЬ НЕ СТИРАЕТСЯ ПРИ ПЕРЕЛИСТЫВАНИИ. Администратор листает дни быстро,
 * и мигание пустой сетки между ними читается как «всё пропало». Поэтому у состояния есть
 * отдельный признак `refreshing`: данные на экране остаются прежними, а рядом честно
 * написано, что идёт загрузка.
 *
 * ПОЧЕМУ ДЕНЬ ПЕРЕЧИТЫВАЕТСЯ САМ. Журнал — общий экран: пока администратор говорит
 * по телефону, клиент на витрине ставит удержание, второй администратор записывает
 * человека, а карантин освобождается по часам. ТЗ §2.4.1 требует, чтобы эти переходы
 * происходили в ОТКРЫТОЙ сетке без перезагрузки страницы, иначе стойка продаёт время,
 * которого уже нет. Поэтому день опрашивается по интервалу и, главное, при возвращении
 * на вкладку: ноутбук закрывали на обед, и первое, что увидит человек, — это правда
 * о текущем часе, а не утренний снимок.
 *
 * ПОЧЕМУ НЕ ОПРАШИВАЕТСЯ СКРЫТАЯ ВКЛАДКА. Фоновая вкладка ничего никому не показывает,
 * а запросы шлёт: десяток открытых кабинетов превращается в постоянную нагрузку на базу
 * ради данных, которых никто не видит.
 *
 * ПОЧЕМУ СБОЙ НЕ СТИРАЕТ ДАННЫЕ. При обрыве связи кабинет продолжает показывать
 * загруженный день и подписывает его моментом из generatedAt («показан день на 14:35»).
 * Пустой экран вместо устаревших данных не помогает никому.
 */

export interface JournalDayState {
  readonly result: ScheduleResult | null
  readonly problem: ApiProblem | null
  /** Первая загрузка: показывать каркас. */
  readonly loading: boolean
  /** Перезагрузка поверх уже показанных данных. */
  readonly refreshing: boolean
}

export interface JournalDay extends JournalDayState {
  readonly reload: () => void
}

/**
 * Как часто перечитывается открытый день. Полминуты — компромисс, названный вслух:
 * реже — и удержание с витрины успевает истечь раньше, чем его увидели; чаще — и сетка
 * дёргается у человека под руками без новых событий.
 */
const POLL_INTERVAL_MS = 30_000

/**
 * Как часто перечитывается НЕДЕЛЯ.
 *
 * Полминуты здесь неуместны по двум причинам. Первая — размер: ответ на семь дней
 * у салона с шестью мастерами идёт мегабайтами, и перекачивать его дважды в минуту
 * значит греть и сеть, и телефон администратора без всякой пользы. Вторая — смысл:
 * неделя отвечает на вопрос «где есть место», а не «что происходит сейчас»; удержание
 * с витрины, ради которого выбраны тридцать секунд в дне, в недельной клетке не
 * различимо вовсе.
 */
const WEEK_POLL_INTERVAL_MS = 5 * 60_000

export function useJournalDay(query: JournalQuery | null): JournalDay {
  const [state, setState] = useState<JournalDayState>({
    result: null,
    problem: null,
    loading: query !== null,
    refreshing: false,
  })
  const [attempt, setAttempt] = useState(0)

  const reload = useCallback(() => {
    setAttempt((value) => value + 1)
  }, [])

  // Ключ, а не сам объект: параметры собираются новым объектом на каждый рендер,
  // и сравнение по ссылке отправляло бы запрос бесконечно.
  const key =
    query === null
      ? null
      : [
          query.branchId,
          query.dateFrom,
          query.dateTo,
          (query.employeeIds ?? []).join(","),
          String(query.includeCancelled ?? false),
        ].join("|")

  useEffect(() => {
    if (query === null || key === null) {
      setState({ result: null, problem: null, loading: false, refreshing: false })
      return
    }

    const controller = new AbortController()
    setState((previous) => ({
      result: previous.result,
      problem: null,
      loading: previous.result === null,
      refreshing: previous.result !== null,
    }))

    const run = async (): Promise<void> => {
      try {
        const result = await fetchJournal(query, controller.signal)
        if (controller.signal.aborted) return
        setState({ result, problem: null, loading: false, refreshing: false })
      } catch (error) {
        // Отменённый запрос — не сбой: день просто перелистнули быстрее, чем он доехал.
        if (controller.signal.aborted) return
        setState((previous) => ({
          result: previous.result,
          problem: isApiProblem(error) ? error : transportProblem("unknown"),
          loading: false,
          refreshing: false,
        }))
      }
    }
    void run()

    return () => {
      controller.abort()
    }
    // query намеренно не в зависимостях: его заменяет key — см. комментарий выше.
  }, [key, attempt])

  useEffect(() => {
    if (key === null) return

    const refreshIfVisible = (): void => {
      if (document.visibilityState !== "visible") return
      reload()
    }
    const isWeek = (query?.dateFrom ?? "") !== (query?.dateTo ?? "")
    const timer = window.setInterval(
      refreshIfVisible,
      isWeek ? WEEK_POLL_INTERVAL_MS : POLL_INTERVAL_MS,
    )
    document.addEventListener("visibilitychange", refreshIfVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener("visibilitychange", refreshIfVisible)
    }
  }, [key, reload, query?.dateFrom, query?.dateTo])

  return { ...state, reload }
}
